import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Callout } from '__types__/v1/models/Callout';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'v5/design-system/Form';
import {
  InputGroup,
  InputGroupInput,
  InputGroupSlot
} from 'v5/design-system/InputGroup';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import { focusRings } from 'v5/design-system/_common/focusRing';
import {
  CalloutSlugFormValues,
  useCalloutSlugField
} from 'v5/features/callouts/useCalloutSlugField';
import { cn } from 'v5/platform/dom/cn';
import { WithServices } from 'v5/platform/ts/services';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithCalloutsService } from 'v5/services/io/CalloutsService/CalloutsService';

export type { CalloutSlugFormValues };

type CalloutSlugFieldProps<T extends CalloutSlugFormValues> = {
  form: UseFormReturn<T>;
  syncSlugFromTitle?: boolean;
  excludeCalloutId?: Callout['id'];
  workspaceId?: number | null;
} & WithServices<
  [WithCalloutsService, WithAppScopeService, WithEnterpriseService]
>;

export function CalloutSlugField<T extends CalloutSlugFormValues>({
  form,
  syncSlugFromTitle = true,
  excludeCalloutId,
  workspaceId,
  services
}: CalloutSlugFieldProps<T>) {
  const { t } = useTranslation('callouts');
  const {
    slugPath,
    slugPrefix,
    validationRules,
    showSyncFromTitleAction,
    handleSlugInputChange,
    enableSyncFromTitle
  } = useCalloutSlugField({
    form,
    syncSlugFromTitle,
    excludeCalloutId,
    workspaceId,
    services
  });

  return (
    <FormField
      control={form.control}
      name={slugPath}
      rules={validationRules}
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex flex-wrap items-center gap-x-1.5">
            <FormLabel>{t('CalloutDialog.slug')}</FormLabel>
            {showSyncFromTitleAction ? (
              <>
                <span
                  className="text-base font-medium leading-none text-fg-muted"
                  aria-hidden="true"
                >
                  ·
                </span>
                <button
                  type="button"
                  className={cn(
                    'has-focus-ring rounded-sm text-base font-medium leading-none text-brand-500 not-disabled:xhover:underline',
                    focusRings.focus.visible
                  )}
                  onClick={() => enableSyncFromTitle({ shouldDirty: true })}
                >
                  {t('CalloutDialog.slugSyncFromTitle')}
                </button>
              </>
            ) : null}
          </div>
          <FormControl>
            <InputGroup className="flex-nowrap overflow-x-auto">
              <InputGroupSlot className="shrink-0 whitespace-nowrap pr-1 text-sm text-fg-muted">
                {slugPrefix}
              </InputGroupSlot>
              <InputGroupInput
                {...field}
                className="min-w-0 flex-1"
                value={field.value ?? ''}
                onChange={event => handleSlugInputChange(event.target.value)}
              />
              {fieldState.isValidating ? (
                <InputGroupSlot className="shrink-0 pr-2 text-fg-muted">
                  <LoadingSpinner className="size-4" />
                </InputGroupSlot>
              ) : null}
            </InputGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
